<template>
  <div class="hall-map">
    <div ref="host" class="hall-map__host">
      <div
        ref="viewport"
        class="hall-map__viewport"
        :style="viewportStyle"
        @pointerdown="startPan"
        @pointermove="movePan"
        @pointerup="stopPan"
        @pointerleave="stopPan"
        @pointercancel="stopPan"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
        @touchcancel="onTouchEnd"
        @wheel.prevent="onWheel"
      >
        <div class="hall-map__canvas" :style="canvasStyle">
          <div class="hall-map__stage" :style="stageStyle">
            <template v-for="(obj, index) in renderLayoutObjects" :key="`obj-${index}`">
              <div
                v-if="obj.type === 'rect'"
                class="hall-obj hall-obj--rect"
                :style="rectStyle(obj)"
              ></div>

              <div
                v-else-if="obj.type === 'label'"
                class="hall-obj hall-obj--label"
                :style="labelStyle(obj)"
              >
                {{ obj.text }}
              </div>

              <div
                v-else-if="obj.type === 'line' || obj.type === 'wall'"
                class="hall-obj"
                :class="obj.type === 'wall' ? 'hall-obj--wall' : 'hall-obj--line'"
                :style="lineStyle(obj)"
              ></div>
            </template>

            <button
              v-for="seat in normalizedSeats"
              :key="seat.id"
              type="button"
              class="hall-seat"
              :class="seatClassObject(seat)"
              :style="seatStyle(seat)"
              :disabled="isDisabled(seat)"
              @pointerdown.stop
              @mousedown.stop
              @touchstart.stop
              @click="handleSeatClick(seat)"
            >
              <span class="hall-seat__label">
                {{ seatLabel(seat) }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function normalizeSeatType(value) {
  const type = String(value || 'pc').trim().toLowerCase();

  if (type === 'console' || type === 'ps' || type === 'playstation') return 'console';
  if (type === 'other') return 'other';

  return 'pc';
}

export default {
  name: 'SeatMap',

  props: {
    layout: {
      type: Object,
      default: null,
    },
    seats: {
      type: Array,
      default: () => [],
    },
    busySeatIds: {
      type: Array,
      default: () => [],
    },
    selectedSeatIds: {
      type: Array,
      default: () => [],
    },
    allowedSeatTypes: {
      type: Array,
      default: () => [],
    },
    selectedSeatId: {
      type: [Number, String, null],
      default: null,
    },
    minViewportHeight: {
      type: [Number, String],
      default: 0,
    },
  },

  emits: ['select'],

  data() {
    return {
      zoom: 1,
      maxZoom: 2.2,
      hostWidth: 360,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,

      isPanning: false,
      panPointerId: null,
      panStartX: 0,
      panStartY: 0,
      panScrollLeft: 0,
      panScrollTop: 0,
      dragMoved: false,

      isTouchPanning: false,
      isPinching: false,
      touchStartX: 0,
      touchStartY: 0,
      touchScrollLeft: 0,
      touchScrollTop: 0,
      pinchStartDistance: 0,
      pinchStartZoom: 1,
    };
  },

  computed: {
    width() {
      return Number(this.layout?.view_box_width || 1200);
    },

    height() {
      return Number(this.layout?.view_box_height || 800);
    },

    isMobile() {
      return this.windowWidth <= 768;
    },

    minZoomValue() {
      return this.isMobile ? 1 : 0.72;
    },

    baseScale() {
      if (!this.hostWidth || !this.width) return 1;
      return this.hostWidth / this.width;
    },

    finalScale() {
      return this.baseScale * this.zoom;
    },

    viewportHeightPx() {
      return Math.round(this.height * this.baseScale);
    },

    viewportStyle() {
      if (this.isMobile) {
        return {
          width: '100%',
          height: '45vh',
          minHeight: '45vh',
          maxHeight: '45vh',
        };
      }

      return {
        height: `${this.viewportHeightPx}px`,
        maxHeight: '72vh',
      };
    },

    canvasStyle() {
      return {
        width: `${Math.round(this.width * this.finalScale)}px`,
        height: `${Math.round(this.height * this.finalScale)}px`,
        minWidth: '100%',
      };
    },

    stageStyle() {
      return {
        width: `${this.width}px`,
        height: `${this.height}px`,
        transform: `scale(${this.finalScale})`,
        transformOrigin: 'top left',
      };
    },

    layoutObjects() {
      const source = this.readLayoutObjects(this.layout);
      return source.map((item) => this.normalizeLayoutObject(item));
    },

    renderLayoutObjects() {
      return this.layoutObjects.filter((obj) => !this.isSeatStyleObject(obj));
    },

    seatRadiusById() {
      const styleObject = this.layoutObjects.find((obj) => this.isSeatStyleObject(obj));
      const source = styleObject?.radius_by_seat
        || styleObject?.radii
        || styleObject?.seats
        || styleObject?.items
        || {};

      return source && typeof source === 'object' ? source : {};
    },

    roomRect() {
      const rects = this.layoutObjects
        .filter((obj) => obj.type === 'rect')
        .map((obj) => ({
          x: this.num(obj.x),
          y: this.num(obj.y),
          w: this.num(obj.w),
          h: this.num(obj.h),
          area: this.num(obj.w) * this.num(obj.h),
        }))
        .filter((obj) => obj.w > 200 && obj.h > 120)
        .sort((a, b) => b.area - a.area);

      if (!rects.length) {
        return {
          x: 0,
          y: 0,
          w: this.width,
          h: this.height,
        };
      }

      return rects[0];
    },

    seatSafeBounds() {
      const room = this.roomRect;
      let topReserved = room.y + 18;

      this.layoutObjects.forEach((obj) => {
        if (obj.type !== 'label') return;

        const y = this.num(obj.y);
        const fontSize = this.num(obj.fontSize || 16);

        if (y <= room.y + 170) {
          topReserved = Math.max(topReserved, y + fontSize + 18);
        }
      });

      return {
        minX: room.x + 14,
        maxX: room.x + room.w - 14,
        minY: topReserved,
        maxY: room.y + room.h - 14,
      };
    },

    normalizedSeats() {
      return (Array.isArray(this.seats) ? this.seats : []).map((seat, index) => {
        const rawW = seat.map_w !== null && seat.map_w !== undefined
          ? Number(seat.map_w)
          : (normalizeSeatType(seat.seat_type) === 'console' ? 86 : 74);

        const rawH = seat.map_h !== null && seat.map_h !== undefined
          ? Number(seat.map_h)
          : (normalizeSeatType(seat.seat_type) === 'console' ? 70 : 54);

        const rawX = seat.map_x !== null && seat.map_x !== undefined
          ? Number(seat.map_x)
          : 70 + ((index % 5) * 94);

        const rawY = seat.map_y !== null && seat.map_y !== undefined
          ? Number(seat.map_y)
          : 150 + (Math.floor(index / 5) * 90);

        return {
          ...seat,
          seat_type: normalizeSeatType(seat.seat_type),
          map_x: rawX,
          map_y: rawY,
          map_w: rawW,
          map_h: rawH,
          map_angle: Number(seat.map_angle || seat.map_rotation || 0),
        };
      });
    },

    selectedSeatIdSet() {
      const ids = Array.isArray(this.selectedSeatIds) && this.selectedSeatIds.length
        ? this.selectedSeatIds
        : [this.selectedSeatId].filter((value) => value !== null && value !== undefined && value !== '');

      return new Set(ids.map((item) => Number(item)));
    },

    busySeatIdSet() {
      return new Set(
        (Array.isArray(this.busySeatIds) ? this.busySeatIds : []).map((item) => Number(item))
      );
    },

    allowedSeatTypeSet() {
      return new Set(
        (Array.isArray(this.allowedSeatTypes) ? this.allowedSeatTypes : [])
          .map((item) => normalizeSeatType(item))
          .filter(Boolean)
      );
    },
  },

  watch: {
    isMobile: {
      immediate: true,
      handler(next) {
        if (next && this.zoom < 1) {
          this.zoom = 1;
        }
      },
    },

    selectedSeatId() {
      this.$nextTick(() => {
        this.centerSelectedSeat();
      });
    },

    selectedSeatIds: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          this.centerSelectedSeat();
        });
      },
    },
  },

  mounted() {
    this.updateHostMetrics();
    window.addEventListener('resize', this.updateHostMetrics, { passive: true });

    this.$nextTick(() => {
      this.centerSelectedSeat();
    });
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateHostMetrics);
  },

  methods: {
    readLayoutObjects(layout) {
      const candidates = [layout?.objects, layout?.objects_json];

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;

        if (typeof candidate === 'string' && candidate.trim()) {
          try {
            const parsed = JSON.parse(candidate);
            if (Array.isArray(parsed)) return parsed;
          } catch (error) {
            // noop
          }
        }
      }

      return [];
    },

    normalizeLayoutObject(obj) {
      if (typeof obj === 'string') {
        try {
          return JSON.parse(obj);
        } catch (error) {
          return { type: 'label', text: obj, x: 0, y: 0 };
        }
      }

      return obj || {};
    },

    isSeatStyleObject(obj) {
      const type = String(obj?.type || '').toLowerCase();
      return type === 'seat_styles' || type === 'pc_styles' || type === 'seat_radius';
    },

    updateHostMetrics() {
      const width = this.$refs.host?.clientWidth || 360;
      this.hostWidth = width;
      this.windowWidth = typeof window !== 'undefined' ? (window.innerWidth || width) : width;

      if (this.zoom < this.minZoomValue) {
        this.zoom = this.minZoomValue;
      }
    },

    num(value) {
      return Number(value || 0);
    },

    clampZoom(value) {
      return Math.min(this.maxZoom, Math.max(this.minZoomValue, value));
    },

    isMainRoomRect(obj) {
      const room = this.roomRect;

      return (
        obj.type === 'rect' &&
        this.num(obj.x) === this.num(room.x) &&
        this.num(obj.y) === this.num(room.y) &&
        this.num(obj.w) === this.num(room.w) &&
        this.num(obj.h) === this.num(room.h)
      );
    },

    rectStyle(obj) {
      if (this.isMainRoomRect(obj)) {
        return {
          left: '0px',
          top: '0px',
          width: `${this.width}px`,
          height: `${this.height}px`,
          borderRadius: '0px',
          background: obj.fill || '#1F2937',
          border: '0',
          boxShadow: 'none',
        };
      }

      return {
        left: `${this.num(obj.x)}px`,
        top: `${this.num(obj.y)}px`,
        width: `${this.num(obj.w)}px`,
        height: `${this.num(obj.h)}px`,
        borderRadius: `${this.num(obj.rx || 0)}px`,
        background: obj.fill || '#1F2937',
        border: `${this.num(obj.strokeWidth || 1)}px solid ${obj.stroke || 'rgba(255,255,255,0.08)'}`,
      };
    },

    labelStyle(obj) {
      return {
        left: `${this.num(obj.x)}px`,
        top: `${this.num(obj.y)}px`,
        width: obj.w !== undefined ? `${this.num(obj.w)}px` : 'auto',
        height: obj.h !== undefined ? `${this.num(obj.h)}px` : 'auto',
        borderRadius: `${this.num(obj.radius ?? obj.rx ?? 0)}px`,
        color: obj.fill || '#F3F4F6',
        fontSize: `${this.num(obj.fontSize || 16)}px`,
        fontWeight: String(obj.fontWeight || 800),
      };
    },

    lineStyle(obj) {
      const hasLineCoords = obj.x1 !== undefined || obj.y1 !== undefined || obj.x2 !== undefined || obj.y2 !== undefined;

      if (!hasLineCoords) {
        return {
          left: `${this.num(obj.x)}px`,
          top: `${this.num(obj.y)}px`,
          width: `${this.num(obj.w || 0)}px`,
          height: `${this.num(obj.h || obj.strokeWidth || (obj.type === 'wall' ? 8 : 3))}px`,
          background: obj.stroke || (obj.type === 'wall' ? '#2F3746' : '#ffffff'),
          borderRadius: `${this.num(obj.radius ?? obj.rx ?? 999)}px`,
        };
      }

      const x1 = this.num(obj.x1);
      const y1 = this.num(obj.y1);
      const x2 = this.num(obj.x2);
      const y2 = this.num(obj.y2);

      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.sqrt((dx * dx) + (dy * dy));
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);

      const thickness = this.num(
        obj.strokeWidth || (obj.type === 'wall' ? 8 : 3)
      );

      return {
        left: `${x1}px`,
        top: `${y1 - (thickness / 2)}px`,
        width: `${length}px`,
        height: `${thickness}px`,
        background: obj.stroke || (obj.type === 'wall' ? '#2F3746' : '#ffffff'),
        transform: `rotate(${angle}deg)`,
        transformOrigin: 'left center',
        borderRadius: `${this.num(obj.radius ?? obj.rx ?? 999)}px`,
      };
    },

    seatLabel(seat) {
      const candidates = [
        seat.map_label_short,
        seat.seat_label,
        seat.seat_badge,
        seat.device_name,
        seat.pc_name,
        seat.pc_number,
        seat.seat_number,
        seat.number,
        seat.place_number,
      ];

      for (const value of candidates) {
        const text = String(value || '').trim();
        if (text) return text;
      }

      return String(seat.id);
    },

    isBusy(seat) {
      return this.busySeatIdSet.has(Number(seat.id));
    },

    isSelected(seat) {
      return this.selectedSeatIdSet.has(Number(seat.id));
    },

    isDisabledByState(seat) {
      return Number(seat.is_bookable || 1) !== 1 || String(seat.seat_status || 'active') !== 'active';
    },

    isDisabledByType(seat) {
      if (!this.allowedSeatTypeSet.size) return false;
      const type = normalizeSeatType(seat?.seat_type);
      return !this.allowedSeatTypeSet.has(type);
    },

    isDisabled(seat) {
      return this.isBusy(seat) || this.isDisabledByState(seat) || this.isDisabledByType(seat);
    },

    seatClassObject(seat) {
      return {
        'hall-seat--busy': this.isBusy(seat),
        'hall-seat--type-disabled': this.isDisabledByType(seat),
        'hall-seat--selected': this.isSelected(seat),
        'hall-seat--console': normalizeSeatType(seat.seat_type) === 'console',
        'hall-seat--circle': seat.map_shape === 'circle',
      };
    },

    seatRadius(seat) {
      const raw = this.seatRadiusById[String(seat.id)];
      return raw !== null && raw !== undefined && raw !== '' ? this.num(raw) : 18;
    },

    seatStyle(seat) {
      const isCircle = seat.map_shape === 'circle';

      if (isCircle) {
        const r = this.num(seat.map_w || 28);

        return {
          left: `${this.num(seat.map_x) - r}px`,
          top: `${this.num(seat.map_y) - r}px`,
          width: `${r * 2}px`,
          height: `${r * 2}px`,
          borderRadius: '999px',
          transform: `rotate(${this.num(seat.map_angle)}deg)`,
        };
      }

      return {
        left: `${this.num(seat.map_x)}px`,
        top: `${this.num(seat.map_y)}px`,
        width: `${this.num(seat.map_w || 74)}px`,
        height: `${this.num(seat.map_h || 54)}px`,
        borderRadius: `${this.seatRadius(seat)}px`,
        transform: `rotate(${this.num(seat.map_angle)}deg)`,
      };
    },

    handleSeatClick(seat) {
      if (this.dragMoved) return;
      if (this.isDisabled(seat)) return;
      this.$emit('select', seat);
    },

    centerSelectedSeat() {
      const viewport = this.$refs.viewport;
      if (!viewport) return;

      const seat = this.normalizedSeats.find((item) => this.isSelected(item));
      if (!seat) return;

      const centerX = (seat.map_shape === 'circle')
        ? this.num(seat.map_x) * this.finalScale
        : (this.num(seat.map_x) + (this.num(seat.map_w || 74) / 2)) * this.finalScale;

      const centerY = (seat.map_shape === 'circle')
        ? this.num(seat.map_y) * this.finalScale
        : (this.num(seat.map_y) + (this.num(seat.map_h || 54) / 2)) * this.finalScale;

      viewport.scrollLeft = Math.max(0, centerX - (viewport.clientWidth / 2));
      viewport.scrollTop = Math.max(0, centerY - (viewport.clientHeight / 2));
    },

    applyZoomAtPoint(nextZoom, clientX, clientY) {
      const viewport = this.$refs.viewport;
      if (!viewport) return;

      const clampedZoom = this.clampZoom(nextZoom);
      const oldScale = this.finalScale;

      if (!oldScale || clampedZoom === this.zoom) return;

      const rect = viewport.getBoundingClientRect();
      const pointX = clientX - rect.left;
      const pointY = clientY - rect.top;

      const contentX = (viewport.scrollLeft + pointX) / oldScale;
      const contentY = (viewport.scrollTop + pointY) / oldScale;

      this.zoom = clampedZoom;

      const newScale = this.baseScale * this.zoom;

      viewport.scrollLeft = Math.max(0, (contentX * newScale) - pointX);
      viewport.scrollTop = Math.max(0, (contentY * newScale) - pointY);
    },

    getTouchDistance(touches) {
      if (!touches || touches.length < 2) return 0;

      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;

      return Math.hypot(dx, dy);
    },

    getTouchCenter(touches) {
      if (!touches || !touches.length) {
        return { x: 0, y: 0 };
      }

      if (touches.length === 1) {
        return {
          x: touches[0].clientX,
          y: touches[0].clientY,
        };
      }

      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
      };
    },

    startPan(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target?.closest('.hall-seat')) return;

      const viewport = this.$refs.viewport;
      if (!viewport) return;

      this.isPanning = true;
      this.panPointerId = event.pointerId;
      this.panStartX = event.clientX;
      this.panStartY = event.clientY;
      this.panScrollLeft = viewport.scrollLeft;
      this.panScrollTop = viewport.scrollTop;
      this.dragMoved = false;

      if (typeof viewport.setPointerCapture === 'function') {
        viewport.setPointerCapture(event.pointerId);
      }
    },

    movePan(event) {
      if (!this.isPanning || event.pointerId !== this.panPointerId) return;

      const viewport = this.$refs.viewport;
      if (!viewport) return;

      const dx = event.clientX - this.panStartX;
      const dy = event.clientY - this.panStartY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.dragMoved = true;
      }

      viewport.scrollLeft = this.panScrollLeft - dx;
      viewport.scrollTop = this.panScrollTop - dy;
    },

    stopPan(event) {
      if (!this.isPanning) return;
      if (event?.pointerId !== undefined && event.pointerId !== this.panPointerId) return;

      const viewport = this.$refs.viewport;

      if (viewport && typeof viewport.releasePointerCapture === 'function' && this.panPointerId !== null) {
        try {
          viewport.releasePointerCapture(this.panPointerId);
        } catch (error) {
          // noop
        }
      }

      this.isPanning = false;
      this.panPointerId = null;

      window.setTimeout(() => {
        this.dragMoved = false;
      }, 0);
    },

    onWheel(event) {
      if (this.isMobile) return;

      const zoomStep = event.deltaY < 0 ? 1.12 : 0.89;
      const nextZoom = this.zoom * zoomStep;

      this.applyZoomAtPoint(nextZoom, event.clientX, event.clientY);
      this.dragMoved = true;

      window.setTimeout(() => {
        this.dragMoved = false;
      }, 0);
    },

    onTouchStart(event) {
      const viewport = this.$refs.viewport;
      if (!viewport) return;

      if (event.touches.length >= 2) {
        this.isPinching = true;
        this.isTouchPanning = false;
        this.pinchStartDistance = this.getTouchDistance(event.touches);
        this.pinchStartZoom = this.zoom;
        this.dragMoved = true;
        return;
      }

      if (event.touches.length === 1) {
        const touch = event.touches[0];

        this.isTouchPanning = true;
        this.isPinching = false;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchScrollLeft = viewport.scrollLeft;
        this.touchScrollTop = viewport.scrollTop;
        this.dragMoved = false;
      }
    },

    onTouchMove(event) {
      const viewport = this.$refs.viewport;
      if (!viewport) return;

      if (event.touches.length >= 2) {
        event.preventDefault();

        const distance = this.getTouchDistance(event.touches);
        if (!this.pinchStartDistance) return;

        const center = this.getTouchCenter(event.touches);
        const scale = distance / this.pinchStartDistance;
        const nextZoom = this.pinchStartZoom * scale;

        this.applyZoomAtPoint(nextZoom, center.x, center.y);
        this.dragMoved = true;
        return;
      }

      if (!this.isTouchPanning || event.touches.length !== 1) return;

      event.preventDefault();

      const touch = event.touches[0];
      const dx = touch.clientX - this.touchStartX;
      const dy = touch.clientY - this.touchStartY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.dragMoved = true;
      }

      viewport.scrollLeft = this.touchScrollLeft - dx;
      viewport.scrollTop = this.touchScrollTop - dy;
    },

    onTouchEnd(event) {
      const viewport = this.$refs.viewport;

      if (event.touches.length >= 2) {
        this.pinchStartDistance = this.getTouchDistance(event.touches);
        this.pinchStartZoom = this.zoom;
        return;
      }

      if (event.touches.length === 1 && viewport) {
        const touch = event.touches[0];

        this.isPinching = false;
        this.isTouchPanning = true;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchScrollLeft = viewport.scrollLeft;
        this.touchScrollTop = viewport.scrollTop;
        return;
      }

      this.isPinching = false;
      this.isTouchPanning = false;
      this.pinchStartDistance = 0;

      window.setTimeout(() => {
        this.dragMoved = false;
      }, 0);
    },
  },
};
</script>

<style scoped src="./SeatMap.css"></style>
